import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconCheck,
  IconCirclePlus,
  IconClock,
  IconPlayerPlay,
  IconX,
} from "@tabler/icons";
import moment from "moment";

import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import {
  describeCronSchedule,
  isValidCronSchedule,
} from "../../utils/cronSchedule";

const apiEntity = "cronJob";
const logEntity = "cronJobExecutionLog";

const categoryColors = {
  Maintenance: "blue",
  RPA: "cyan",
  System: "gray",
};

const CategoryBadge = ({ category }) => {
  if (!category) return null;
  return (
    <Badge
      size="xs"
      variant="filled"
      color={categoryColors[category] || "gray"}
    >
      {category}
    </Badge>
  );
};

const TriggerBadge = ({ trigger }) => {
  const color = trigger === "MANUAL" ? "violet" : "gray";
  return (
    <Badge size="xs" variant="light" color={color}>
      {trigger || "SCHEDULED"}
    </Badge>
  );
};

const formatDate = (value) => {
  if (!value) return "-";
  return moment(value).format("YYYY-MM-DD HH:mm:ss");
};

const formatDuration = (ms) => {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const truncate = (text, max = 60) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

const StatusBadge = ({ status }) => {
  const colors = {
    SUCCESS: "green",
    ERROR: "red",
    RUNNING: "yellow",
    IDLE: "gray",
  };
  return <Badge color={colors[status] || "gray"}>{status || "IDLE"}</Badge>;
};

const SchedulePreview = ({ expression }) => {
  if (!expression?.trim()) return null;
  const valid = isValidCronSchedule(expression);
  const label = describeCronSchedule(expression);
  return (
    <Text size="xs" color={valid ? "dimmed" : "red"} mt={4}>
      Runs: {label}
    </Text>
  );
};

const CronJobPage = () => {
  const [api] = useServerApi();

  const [activeTab, setActiveTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [runConfirm, setRunConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formName, setFormName] = useState("");
  const [formSchedule, setFormSchedule] = useState("0 0 * * *");
  const [formDescription, setFormDescription] = useState("");
  const [formActive, setFormActive] = useState(false);

  const metadataByName = useMemo(() => {
    const map = {};
    for (const item of available) {
      map[item.name] = item;
    }
    return map;
  }, [available]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const [jobsResult, availableResult] = await Promise.all([
        api.search({
          apiEntity,
          pageSize: 200,
          currentPage: 1,
          searchQuery: {},
          sort: { by: "name", order: "asc" },
          byAggregation: false,
        }),
        api.CronJob.listAvailable(),
      ]);
      setJobs(jobsResult?.docs ?? []);
      setAvailable(availableResult?.data?.docs ?? []);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const result = await api.search({
        apiEntity: logEntity,
        pageSize: 200,
        currentPage: 1,
        searchQuery: {},
        sort: { by: "startedAt", order: "desc" },
        byAggregation: false,
      });
      setLogs(result?.docs ?? []);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (activeTab === "logs") {
      loadLogs();
    }
  }, [activeTab]);

  const getJobDescription = (job) => {
    if (job.description?.trim()) return job.description.trim();
    return metadataByName[job.name]?.description || "";
  };

  const existingNames = useMemo(
    () => new Set(jobs.map((job) => job.name)),
    [jobs]
  );

  const addableOptions = useMemo(
    () =>
      available
        .filter((item) => !existingNames.has(item.name))
        .map((item) => ({
          value: item.name,
          label: item.description
            ? `${item.name} — ${truncate(item.description, 50)}`
            : item.name,
        })),
    [available, existingNames]
  );

  const resetCreateForm = () => {
    setFormName("");
    setFormSchedule("0 0 * * *");
    setFormDescription("");
    setFormActive(false);
  };

  const handleJobSelect = (name) => {
    setFormName(name);
    if (name && metadataByName[name]?.description) {
      setFormDescription(metadataByName[name].description);
    }
  };

  const openEdit = (job) => {
    setEditJob(job);
    setFormSchedule(job.schedule || "0 0 * * *");
    setFormDescription(getJobDescription(job));
    setFormActive(Boolean(job.active));
  };

  const handleCreate = async () => {
    if (!formName) {
      showNotification({
        title: "Job name is required",
        color: "red",
        icon: <IconX size={18} />,
        message: "Select a job handler to add.",
      });
      return;
    }

    if (!isValidCronSchedule(formSchedule)) {
      showNotification({
        title: "Invalid schedule",
        color: "red",
        icon: <IconX size={18} />,
        message: "Enter a valid cron expression.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.CronJob.create({
        name: formName,
        schedule: formSchedule.trim(),
        description: formDescription.trim(),
        active: formActive,
      });
      if (!result?.success) {
        showNotification({
          title: "Failed to add cron job",
          color: "red",
          icon: <IconX size={18} />,
          message: result?.message ?? "Unknown error",
        });
        return;
      }
      setCreateOpen(false);
      resetCreateForm();
      loadJobs();
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editJob) return;

    if (!isValidCronSchedule(formSchedule)) {
      showNotification({
        title: "Invalid schedule",
        color: "red",
        icon: <IconX size={18} />,
        message: "Enter a valid cron expression.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.updateById({
        apiEntity,
        id: editJob._id,
        values: {
          schedule: formSchedule.trim(),
          description: formDescription.trim(),
          active: formActive,
        },
      });
      if (!result?.success) {
        showNotification({
          title: "Failed to update cron job",
          color: "red",
          icon: <IconX size={18} />,
          message: result?.message ?? "Unknown error",
        });
        return;
      }
      setEditJob(null);
      loadJobs();
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (job) => {
    const result = await api.CronJob.toggleActive(job._id);
    if (!result?.success) {
      showNotification({
        title: "Failed to toggle cron job",
        color: "red",
        icon: <IconX size={18} />,
        message: result?.message ?? "Unknown error",
      });
      return;
    }
    loadJobs();
  };

  const handleRunNow = async (job) => {
    setSubmitting(true);
    try {
      const result = await api.CronJob.runNow(job._id);
      if (!result?.success) {
        showNotification({
          title: "Failed to run cron job",
          color: "red",
          icon: <IconX size={18} />,
          message: result?.message ?? "Unknown error",
        });
        return;
      }
      showNotification({
        title: "Cron job finished",
        color: result.data?.result?.success === false ? "red" : "green",
        icon: <IconCheck size={18} />,
        message:
          result.data?.result?.success === false
            ? result.data?.result?.error || "Job failed"
            : `${job.name} completed successfully`,
      });
      setRunConfirm(null);
      loadJobs();
      if (activeTab === "logs") {
        loadLogs();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page
      title={"Cron Jobs"}
      actions={
        activeTab === "jobs" ? (
          <Button
            leftIcon={<IconCirclePlus size={16} />}
            onClick={() => {
              resetCreateForm();
              setCreateOpen(true);
            }}
            disabled={addableOptions.length === 0}
          >
            Add Job
          </Button>
        ) : (
          <Button variant="light" onClick={loadLogs} loading={logsLoading}>
            Refresh
          </Button>
        )
      }
    >
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="jobs">Cron Jobs</Tabs.Tab>
          <Tabs.Tab value="logs">Execution Logs</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="jobs" pt="md">
          <Text size="sm" color="dimmed" mb="md">
            View and manage scheduled background jobs. Changes apply immediately
            without restarting the server. Job handlers are defined in server
            code; this page controls schedules and enablement.
          </Text>

          <Table striped withBorder verticalSpacing="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Schedule</th>
                <th>Active</th>
                <th>Last Run</th>
                <th>Status</th>
                <th>Duration</th>
                <th style={{ width: 200, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && !loading && (
                <tr>
                  <td colSpan={8}>
                    <Text align="center" color="dimmed" py="md">
                      No cron jobs configured yet.
                    </Text>
                  </td>
                </tr>
              )}
              {jobs.map((job) => {
                const description = getJobDescription(job);
                const scheduleLabel = describeCronSchedule(job.schedule);
                const category = metadataByName[job.name]?.category;
                return (
                  <tr key={job._id}>
                    <td>
                      <Group spacing="xs" noWrap align="flex-start">
                        <IconClock size={16} style={{ marginTop: 2 }} />
                        <span>
                          <Text size="sm" weight={500}>
                            {job.name}
                          </Text>
                          {description ? (
                            <Text size="xs" color="dimmed" lineClamp={2} mt={4}>
                              {description}
                            </Text>
                          ) : null}
                        </span>
                      </Group>
                    </td>
                    <td>
                      <CategoryBadge category={category} />
                    </td>
                    <td>
                      <Text size="xs">{scheduleLabel}</Text>
                      <Text
                        size="xs"
                        color="dimmed"
                        style={{ fontFamily: "monospace" }}
                      >
                        {job.schedule}
                      </Text>
                    </td>
                    <td>
                      <Switch
                        checked={Boolean(job.active)}
                        onChange={() => handleToggle(job)}
                        disabled={job.lastStatus === "RUNNING"}
                      />
                    </td>
                    <td>
                      <Text size="xs">{formatDate(job.lastRunAt)}</Text>
                    </td>
                    <td>
                      <StatusBadge status={job.lastStatus} />
                      {job.lastError ? (
                        <Text size="xs" color="red" lineClamp={2}>
                          {job.lastError}
                        </Text>
                      ) : null}
                    </td>
                    <td>
                      <Text size="xs">{formatDuration(job.lastDurationMs)}</Text>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Group spacing="xs" position="right" noWrap>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => openEdit(job)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          leftIcon={<IconPlayerPlay size={14} />}
                          onClick={() => setRunConfirm(job)}
                          disabled={job.lastStatus === "RUNNING"}
                        >
                          Run Now
                        </Button>
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel value="logs" pt="md">
          <Text size="sm" color="dimmed" mb="md">
            History of cron job executions, including scheduled runs and manual
            triggers.
          </Text>

          <Table striped withBorder verticalSpacing="sm">
            <thead>
              <tr>
                <th>Job</th>
                <th>Category</th>
                <th>Trigger</th>
                <th>Started</th>
                <th>Finished</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && !logsLoading && (
                <tr>
                  <td colSpan={8}>
                    <Text align="center" color="dimmed" py="md">
                      No execution logs yet.
                    </Text>
                  </td>
                </tr>
              )}
              {logs.map((log) => (
                <tr key={log._id}>
                  <td>
                    <Text size="sm" weight={500}>
                      {log.jobName}
                    </Text>
                  </td>
                  <td>
                    <CategoryBadge
                      category={metadataByName[log.jobName]?.category}
                    />
                  </td>
                  <td>
                    <TriggerBadge trigger={log.trigger} />
                  </td>
                  <td>
                    <Text size="xs">{formatDate(log.startedAt)}</Text>
                  </td>
                  <td>
                    <Text size="xs">{formatDate(log.finishedAt)}</Text>
                  </td>
                  <td>
                    <StatusBadge status={log.status} />
                  </td>
                  <td>
                    <Text size="xs">{formatDuration(log.durationMs)}</Text>
                  </td>
                  <td>
                    {log.error ? (
                      <Text size="xs" color="red" lineClamp={2}>
                        {log.error}
                      </Text>
                    ) : (
                      <Text size="xs" color="dimmed">
                        -
                      </Text>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={createOpen}
        onClose={() => {
          if (submitting) return;
          setCreateOpen(false);
          resetCreateForm();
        }}
        title={<Title order={4}>Add Cron Job</Title>}
      >
        <Stack>
          <Select
            label="Job"
            placeholder="Select a handler"
            data={addableOptions}
            value={formName}
            onChange={handleJobSelect}
            searchable
            required
          />
          <div>
            <TextInput
              label="Schedule"
              description="Cron expression (minute hour day month weekday), e.g. 0 3 * * *"
              value={formSchedule}
              onChange={(e) => setFormSchedule(e.currentTarget.value)}
              required
            />
            <SchedulePreview expression={formSchedule} />
          </div>
          <Textarea
            label="Description"
            description="Pre-filled from job metadata; edit if needed."
            placeholder="What does this job do?"
            value={formDescription}
            onChange={(e) => setFormDescription(e.currentTarget.value)}
            minRows={2}
          />
          <Switch
            label="Active"
            checked={formActive}
            onChange={(e) => setFormActive(e.currentTarget.checked)}
          />
          <Group position="right">
            <Button
              variant="default"
              onClick={() => setCreateOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={submitting}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(editJob)}
        onClose={() => {
          if (submitting) return;
          setEditJob(null);
        }}
        title={<Title order={4}>Edit Cron Job</Title>}
      >
        <Stack>
          <TextInput label="Job" value={editJob?.name || ""} disabled />
          <div>
            <TextInput
              label="Schedule"
              description="Cron expression (minute hour day month weekday)"
              value={formSchedule}
              onChange={(e) => setFormSchedule(e.currentTarget.value)}
              required
            />
            <SchedulePreview expression={formSchedule} />
          </div>
          <Textarea
            label="Description"
            description="What does this job do?"
            value={formDescription}
            onChange={(e) => setFormDescription(e.currentTarget.value)}
            minRows={2}
          />
          <Switch
            label="Active"
            checked={formActive}
            onChange={(e) => setFormActive(e.currentTarget.checked)}
          />
          <Group position="right">
            <Button variant="default" onClick={() => setEditJob(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(runConfirm)}
        onClose={() => setRunConfirm(null)}
        title={<Title order={4}>Run cron job now?</Title>}
      >
        <Stack>
          <Text size="sm">
            Run <b>{runConfirm?.name}</b> immediately? This may take a while for
            heavy jobs.
          </Text>
          {runConfirm ? (
            <Text size="xs" color="dimmed">
              {getJobDescription(runConfirm)}
            </Text>
          ) : null}
          <Group position="right">
            <Button variant="default" onClick={() => setRunConfirm(null)}>
              Cancel
            </Button>
            <Button
              color="blue"
              loading={submitting}
              onClick={() => handleRunNow(runConfirm)}
            >
              Run Now
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Page>
  );
};

export default CronJobPage;
